import React, { useEffect, useRef } from 'react'


import { Button } from '@mui/material'
import { CommonFilters, AppNote } from '@components'
import {
	analyzerDate, offices, reports, reportsInfo, reportsLoaded, repricingPass, repricingPassesInfo, repricingPassesLoaded,
	repricingPassesReload,
	searchID, sites,
} from '@signals'
import {analyzerCarPick, analyzerNotFound, analyzerOptionFilters, hasNoCurrencyResults, isAnalyzerLoading, resetSignals, tableTabSelected} from 'Pages/AnalyzersV2/@signals'
import { ALL_CARS } from 'Pages/AnalyzersV2/@services/carPick'
import { BACKEND_APIS } from '@constants'
import { linkNotFound, runningNote } from 'Pages/@PageWrapper/Actions/@services/executionOptions'
import { mainProvider, mainSite } from '@signals'
import { setMainSite } from '@components/CommonFilters/@components/Autocomplete/MainSites'
import { identity, identityState, loadIdentity } from 'Pages/User/Settings/@signals/account'
import { contextKey, isUserClear, ownDefault, ownIds } from 'Pages/AnalyzersV2/@services/mainDefault'

// The identity (Settings › Account) once its request has answered, either way
const identitySettled = () => new Promise(resolve => {
	let done = false
	let stop = null
	stop = identityState.subscribe(state => {
		if (done || state === 'idle' || state === 'loading') return
		done = true
		resolve()
		stop?.()
	})
	if (done) stop()
})

/** "We can't find this execution" (criterion 9, UX Obs. 9): a linked pass or report the account's lists do not hold,
 *  or a 404 from the engine. Nothing is requested and no price is drawn. */
export const executionNotFound = () => analyzerNotFound.value || linkNotFound({
	pass: repricingPass.value, passes: repricingPassesInfo.value, passesLoaded: repricingPassesLoaded.value,
	report: repricingPass.value ? null : reports.value, reports: reportsInfo.value, reportsLoaded: reportsLoaded.value,
})

export default function AnalyzerCommonFilters({ onSubmit, components, schema, isValid, errors, compProps = {}, getDirectSites }) {

	useEffect(() => {
		resetSignals()
		analyzerNotFound.value = false
		return () => {
			isAnalyzerLoading.value = false
		}
	}, [reports.value, repricingPass.value])

	// a new report, pass or office opens on All cars (CU-869fmpqfm, criterion 11); nothing is asked until Search
	useEffect(() => {
		analyzerCarPick.value = ALL_CARS
	}, [reports.value, repricingPass.value, offices.value])

	const hasValidDates = Array.isArray(analyzerDate.value)
		? analyzerDate.value.length > 0
		: !!analyzerDate.value


	// Search again when Min / Max changes. Not on mount: coming from another analyzer, its filters are
	// still in the signals until this page resets them, and they would launch a search nobody asked for.
	const isMounted = useRef(false)
	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true
			return
		}
		if ((isValid && (!!reports.value || !!repricingPass.value)) || (!!searchID.value && hasValidDates)) {
			// the table on screen stays under "Updating…" (CU-869fmpqfm C1): Min / Max is not a new search
			onSubmit(false, { keep: true })
		}
	}, [tableTabSelected.value])

	const hasDirectSites = window.location.pathname.includes('hasDirectSites')
	const hasBrokers = window.location.pathname.includes('hasCompetitionSites')
	// a repricing pass is sent under its own key, never as `reportExecutionID` (CU-869fmm944)
	const pass = repricingPass.value
	const reportExecutionID = pass ? null : reports.value
	const searchIDUsed = searchID.value
	const officePickupID = offices.value
	const siteID = sites.value
	const notFound = executionNotFound()
	const hasOfficeOrSearch = !notFound && ((!!officePickupID && (!!reportExecutionID || !!pass)) || !!searchIDUsed)
	const passItem = pass ? repricingPassesInfo.value.find(p => p.execution_id === pass) : null
	const running = runningNote(passItem)

	// "Main providers" / "Main sites" start on the account's own brand / site (Settings › Account) when empty
	// (CU-869fmm949): on each load of the field's list; a value the user cleared stays cleared in this context (a new
	// execution, pass, search or office opens it again)
	useEffect(() => { loadIdentity() }, [])
	const mainContext = useRef('')
	mainContext.current = contextKey({ report: reportExecutionID, pass, searchID: searchIDUsed, office: officePickupID })
	const mainCleared = useRef({ provider: null, site: null })
	const onMainPick = field => value => {
		if (isUserClear(value)) mainCleared.current[field] = mainContext.current
	}
	const prefillMain = field => async options => {
		if (identityState.peek() === 'idle' || identityState.peek() === 'loading') await identitySettled()
		const own = ownIds(identity.peek())
		const isProvider = field === 'provider'
		const value = ownDefault({
			current: isProvider ? mainProvider.peek() : mainSite.peek(),
			own: isProvider ? own.providers : own.sites,
			options,
			dismissed: mainCleared.current[field] === mainContext.current,
		})
		if (value == null) return
		if (isProvider) mainProvider.value = value
		else setMainSite(value)
	}

	// The pathname flags are how the older analyzers declare which family of webs they read;
	// a page with a Direct Sites / Broker toggle passes the current mode instead. Every option
	// of `/master-data/execution/options/*` is scoped by it, not only the site list: without it
	// the dates, LORs, providers or acriss of the other family would still be offered.
	const directSitesFilter = getDirectSites ?? ((hasDirectSites || hasBrokers) ? hasDirectSites : undefined)

	const filterCommonDependencies = {
		filters: {
			...!searchIDUsed ? { officePickupID } : { searchID: searchIDUsed },
			...directSitesFilter != null && { getDirectSites: directSitesFilter },
			reportExecutionID,
			...pass && { repricingExecutionID: pass },
		},
	}

	const filtersWithSiteAndMain = {
		...filterCommonDependencies.filters,
		siteID: siteID,
	}

	const autocompleteProps = {
		...filterCommonDependencies,
	}

	// the Cars selector above the table lists the cars of this execution, office and site, like the band (CU-869fmpqfm)
	const optionFiltersKey = JSON.stringify(filtersWithSiteAndMain)
	useEffect(() => {
		analyzerOptionFilters.value = filtersWithSiteAndMain
	}, [optionFiltersKey])

	const noResults = hasNoCurrencyResults.value
	const disabledByNoResults = hasOfficeOrSearch && noResults ? { disabled: true } : {}

	const gridProps = { xs: 2 }
	return (
		<>
		<CommonFilters
			components={{
				priceKpiToggle: hasOfficeOrSearch,
				webProviderToggle: hasOfficeOrSearch,
				offices: !notFound && (!!reportExecutionID || !!pass),
				mainSite: hasOfficeOrSearch,
				sites: hasOfficeOrSearch,
				categories: hasOfficeOrSearch,
				providers: hasOfficeOrSearch,
				tiers: hasOfficeOrSearch,
				currency: hasOfficeOrSearch,
				showFeatures: hasOfficeOrSearch,
				analyzerAcriss: hasOfficeOrSearch,
				mileage: hasOfficeOrSearch,
				transmission: hasOfficeOrSearch,
				fuelPolicy: hasOfficeOrSearch,
				insurance: hasOfficeOrSearch,
				prepaid: hasOfficeOrSearch,
				inTerminal: hasOfficeOrSearch,
				webSuppliers: hasOfficeOrSearch,
				// Plain optional multi select: the analyzers have no negation for
				// `id_master_web_supplier` (only reports extras do), so the
				// include/exclude selector stays out.
				hideExcludeWebSuppliers: true,
				// TEMPORARY: hidden until the backend decides what an empty dropoff
				// means (see `_set_optional_filters` in base_filters_service.py). The
				// slot and the payload key stay wired, so re-enabling is one line.
				dropoffOffices: false,
				dates: hasOfficeOrSearch,
				mainProvider: hasOfficeOrSearch,
				...components,
			}}
			compProps={{
				// The toggle labels are analyzer-specific (position renames the two
				// modes), so whatever the page sets goes straight through.
				directSiteBrokerProviderToggle: { ...compProps?.directSiteBrokerProviderToggle },
				showFeatures: { ...compProps?.showFeatures },
				offices: {
					error: errors.officePickupID,
					notAsync: true,
					...pass
						? { urlAjax: `${BACKEND_APIS.GENERAL_REPRICING_API}/analyzer/offices`, filters: { repricingExecutionID: pass, selected: officePickupID } }
						: { urlAjax: `${BACKEND_APIS.EXECUTION_INFO}/offices`, filters: { idReportExecution: reportExecutionID, selected: officePickupID } },
				},
				mainSite: {
					...autocompleteProps,
					...disabledByNoResults,
					onFinishAjax: prefillMain('site'),
					onPick: onMainPick('site'),
					// `siteMainID` is the payload key: analyzers that validate it
					// directly (position) surface the error under that name.
					error: errors.mainSite || errors.siteMainID,
					...hasDirectSites && { label: 'Main Site' },
					filters: { ...filtersWithSiteAndMain, siteID: null },
					...compProps?.mainSite,
					...(compProps?.mainSite?.filters && {
						filters: { ...filtersWithSiteAndMain, siteID: null, ...compProps.mainSite.filters },
					}),
				},
				sites: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.sites || errors.siteID,
					multiple: !hasBrokers,
					...compProps?.sites,
					...(compProps?.sites?.filters && {
						filters: { ...filterCommonDependencies.filters, ...compProps.sites.filters },
					}),
				},
				categories: {
					...autocompleteProps,
					...disabledByNoResults,
					// `categoryID` is the payload key: One Way validates it directly when the
					// header groups by category, and surfaces the error under that name.
					error: errors.categories || errors.categoryID,
					filters: filtersWithSiteAndMain,
					...compProps?.categories,
				},
				// Only the width and the layout order: the options come from the analyzer's own
				// group by dimension, which needs no execution scoped ajax.
				acrissGroupBy: { ...compProps?.acrissGroupBy },
				mainProvider: {
					...autocompleteProps,
					...disabledByNoResults,
					onFinishAjax: prefillMain('provider'),
					onPick: onMainPick('provider'),
					grid: { xs: 3 },
					error: errors.mainProvider || errors.providerMainID,
					filters: filtersWithSiteAndMain,
					...compProps?.mainProvider,
					...(compProps?.mainProvider?.filters && {
						filters: { ...filtersWithSiteAndMain, ...compProps.mainProvider.filters },
					}),
				},
				providers: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.providerID,
					grid: { ...gridProps, ...compProps?.providers?.grid },
					filters: filtersWithSiteAndMain,
				},
				tiers: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.tiers,
					grid: gridProps,
					// a per-user list: the execution filters only made it re-fetch on every office, site or toggle change
					filters: {},
				},
				dates: {
					...disabledByNoResults,
					error: errors.dates,
					disableClearable: true,
					label: 'Pickup Date',
					multiple: false,
					filters: filtersWithSiteAndMain,
					...compProps.dates,
				},
				lengthRental: {
					...autocompleteProps,
					...disabledByNoResults,
					grid: { ...gridProps, ...compProps?.lengthRental?.grid },
					filters: filtersWithSiteAndMain,
					...compProps?.lengthRental,
				},
				currency: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.currency,
					filters: filtersWithSiteAndMain,
					...compProps?.currency,
					onFinishAjax: options => { hasNoCurrencyResults.value = options.length === 0 },
				},
				analyzerAcriss: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.acriss,
					filters: filtersWithSiteAndMain,
					...compProps?.analyzerAcriss,
				},
				acrissGroups: {
					...disabledByNoResults,
					error: errors.acrissGroupID,
					...compProps?.acrissGroups,
				},
				mileage: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.mileage,
					filters: filtersWithSiteAndMain,
				},
				transmission: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.transmission,
					filters: filtersWithSiteAndMain,
				},
				fuelPolicy: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.fuelPolicy,
					filters: filtersWithSiteAndMain,
				},
				insurance: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.insurance,
					// filtered by site only on the backend; the office/execution keys just caused refetches
					filters: { siteID },
				},
				prepaid: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.prepaid,
					filters: filtersWithSiteAndMain,
				},
				inTerminal: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.inTerminal,
					// a static list: no filter is read on the backend
					filters: {},
				},
				webSuppliers: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.webSupplierID,
					grid: { xs: 3 },
					// The shared component defaults to the user-scoped list, which the
					// reports drawer needs; analyzers list what the execution actually has.
					urlAjax: `${BACKEND_APIS.MASTER_DATA}/execution/options/web_suppliers`,
					filters: filtersWithSiteAndMain,
				},
				dropoffOffices: {
					...autocompleteProps,
					...disabledByNoResults,
					error: errors.officeDropoffID,
					filters: filtersWithSiteAndMain,
					...compProps?.dropoffOffices,
				},
			}}
			isValid={isValid}
			onSubmit={() => onSubmit(true)}
			schema={schema}
			isLoading={isAnalyzerLoading.value}
		/>
		{notFound && (
			<AppNote severity="warning" sx={{ mt: 1 }}>
				<b>We can't find this execution.</b> It may belong to another account or be older than we keep. No prices are shown. Choose an execution in the field above.
			</AppNote>
		)}
		{!notFound && running && (
			<AppNote severity="warning" sx={{ mt: 1 }}>
				{running} <Button size="small" variant="text" sx={{ textTransform: 'none', py: 0 }} onClick={() => { repricingPassesReload.value += 1; if (isValid) onSubmit(false) }}>Refresh</Button>
			</AppNote>
		)}
		{noResults && (
			<AppNote severity="warning" sx={{ mt: 1 }}>
				No results available for this office. Change office or execution to get results.
			</AppNote>
		)}
		</>
	)
}
